import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resourceAPI } from '../lib/api';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Search, BookOpen, Wrench, Code, GraduationCap, Loader2, ExternalLink, Eye } from 'lucide-react';

const TYPE_ICONS = {
  tutorial: BookOpen,
  tool: Wrench,
  api: Code,
  learning_path: GraduationCap,
};

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced: 'bg-red-100 text-red-700',
};

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const type = searchParams.get('type') || '';
  const difficulty = searchParams.get('difficulty') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    fetchResources();
  }, [type, difficulty, search]);

  const fetchResources = async () => {
    setIsLoading(true);
    try {
      const response = await resourceAPI.getAll({ 
        type: type || undefined,
        difficulty: difficulty || undefined,
        search: search || undefined
      });
      setResources(response.data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchValue = formData.get('search');
    const newParams = new URLSearchParams(searchParams);
    if (searchValue) {
      newParams.set('search', searchValue);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="resources-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Resources</h1>
          <p className="text-slate-600 mt-1">Tutorials, tools, and learning materials for hackers</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                name="search"
                placeholder="Search resources..."
                defaultValue={search}
                className="pl-10 bg-white"
              />
            </div>
          </form>

          <Select value={type || 'all'} onValueChange={(v) => {
            const newParams = new URLSearchParams(searchParams);
            if (v && v !== 'all') newParams.set('type', v);
            else newParams.delete('type');
            setSearchParams(newParams);
          }}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="tutorial">Tutorials</SelectItem>
              <SelectItem value="tool">Tools</SelectItem>
              <SelectItem value="api">APIs</SelectItem>
              <SelectItem value="learning_path">Learning Paths</SelectItem>
            </SelectContent>
          </Select>

          <Select value={difficulty || 'all'} onValueChange={(v) => {
            const newParams = new URLSearchParams(searchParams);
            if (v && v !== 'all') newParams.set('difficulty', v);
            else newParams.delete('difficulty');
            setSearchParams(newParams);
          }}>
            <SelectTrigger className="w-[180px] bg-white">
              <SelectValue placeholder="Difficulty" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Resources Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No resources found</h3>
            <p className="text-slate-600">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => {
              const TypeIcon = TYPE_ICONS[resource.type] || BookOpen;
              return (
                <Card key={resource.resource_id} className="card-hover">
                  <CardContent className="p-5">
                    {/* Type Icon */}
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                      <TypeIcon className="w-5 h-5 text-indigo-600" />
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="secondary">{resource.type}</Badge>
                      <Badge className={DIFFICULTY_COLORS[resource.difficulty] || 'bg-slate-100'}>
                        {resource.difficulty}
                      </Badge>
                    </div>

                    {/* Title */}
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {resource.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">
                      {resource.description}
                    </p>

                    {/* Tags */}
                    {resource.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {resource.tags.slice(0, 3).map((tag) => (
                          <Badge key={tag} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Eye className="w-4 h-4" />
                        <span>{resource.views || 0} views</span>
                      </div>
                      {resource.url && (
                        <a
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          Open <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
